import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocale } from "../lib/useLocale";
import { PartnerSection } from "../components/PartnerSection";
import { ArrowRight, ArrowUpRight, ShieldCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1646894232861-a0ad84f1ad5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const BRAND = "https://images.unsplash.com/photo-1580445206726-c6eace8e02e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const DEST_IMGS = [
  "https://images.unsplash.com/photo-1602094395895-fb8070114e38?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", // India
  "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", // Thailand
  "https://images.unsplash.com/photo-1558005530-a7958896ec60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", // Bali
  "https://images.unsplash.com/photo-1560243816-0efd9c666469?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", // Portugal
  "https://images.unsplash.com/photo-1634942062514-6e00d236759d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", // Greece
];

export default function RokertStory() {
  const { t } = useTranslation();
  const { path: lp } = useLocale();
  const arr = (x) => (Array.isArray(x) ? x : []);
  const sections = arr(t("story.sections", { returnObjects: true }));
  const future = t("land.future", { returnObjects: true });
  const destinations = arr(future.list);

  return (
    <div data-testid="story-page">
      <Helmet>
        <title>{t("story.metaTitle")}</title>
        <meta name="description" content={t("story.metaDesc")} />
        <meta property="og:title" content={t("story.metaTitle")} />
        <meta property="og:description" content={t("story.metaDesc")} />
      </Helmet>

      {/* HERO with animated emblem */}
      <section className="relative h-[86vh] min-h-[600px] w-full overflow-hidden -mt-[74px] grain">
        <img src={HERO} alt="Rokert" className="absolute inset-0 w-full h-full object-cover slow-zoom" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-4xl mx-auto px-6 md:px-10 flex flex-col justify-center items-center text-center">
          <div className="fade-up">
            <img src="/media/rokert-emblem.png" alt="Rokert emblem" className="h-24 md:h-32 w-auto" />
          </div>
          <span className="overline mt-8 fade-up" style={{ animationDelay: "120ms" }}>{t("story.heroOverline")}</span>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight mt-5 text-rr-ivory fade-up" style={{ animationDelay: "220ms" }}>
            {t("story.heroTitle")}
          </h1>
          <p className="text-rr-ivory/80 text-base md:text-lg max-w-xl mt-7 leading-relaxed fade-up" style={{ animationDelay: "340ms" }}>{t("story.heroSub")}</p>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-rr-ivory/50 scroll-cue">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        </div>
      </section>

      {/* STORY — editorial */}
      <section className="max-w-3xl mx-auto px-6 md:px-8 py-24 md:py-32 space-y-20">
        {sections.map((s, i) => (
          <div key={s.heading} data-testid={`story-section-${i}`} className="fade-up">
            <div className="hairline mb-6" />
            <h2 className="font-heading text-3xl md:text-4xl text-rr-ivory">{s.heading}</h2>
            <p className="text-rr-ivory/70 text-base md:text-lg leading-relaxed mt-5">{s.body}</p>
            {i === 1 && (
              <div className="mt-14 relative h-[360px] md:h-[460px] overflow-hidden rounded-xl">
                <img src={BRAND} alt="Rokert ambience" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 hero-overlay-soft" />
              </div>
            )}
          </div>
        ))}
      </section>

      {/* FUTURE DESTINATIONS — grand cards */}
      <section data-testid="future-destinations" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-28">
        <div className="mb-12">
          <span className="overline">{future.overline}</span>
          <h2 className="font-heading text-4xl md:text-5xl mt-3 text-rr-ivory">{t("invest.futureTitle")}</h2>
          <p className="text-rr-ivory/60 text-base mt-4 max-w-xl">{future.text}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {destinations.map((d, i) => (
            <div key={d} data-testid={`destination-${i}`} className="group relative h-[300px] rounded-xl overflow-hidden card-lift">
              <img src={DEST_IMGS[i]} alt={d} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
              <div className="absolute inset-0 hero-overlay-soft" />
              <div className="absolute top-4 left-4">
                <span className="text-[9px] uppercase tracking-[0.22em] text-rr-bg bg-rr-gold rounded-full px-3 py-1">{future.comingSoon}</span>
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <h3 className="font-heading text-2xl text-rr-ivory">{d}</h3>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INVESTOR / PARTNER FORM */}
      <PartnerSection />

      {/* REG + CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-8 py-24 text-center border-t border-rr-line">
        <div className="flex items-center justify-center gap-2 text-rr-ivory/50 text-sm mb-6">
          <ShieldCheck className="w-4 h-4 text-rr-gold/70" strokeWidth={1.6} />
          <span data-testid="story-reg">{t("story.reg")}</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={lp("/retreats")} data-testid="story-retreats-cta" className="btn-gold px-9 py-4 flex items-center justify-center gap-2 text-xs uppercase">
            {t("story.cta")} <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
          <a href="#partners" data-testid="story-partner-cta" className="btn-outline px-9 py-4 flex items-center justify-center gap-2 text-xs uppercase">
            {t("story.partnerCta")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
          </a>
        </div>
      </section>
    </div>
  );
}
